// What a company needs in its profile before it can sell goods: read off the
// company's own settings, shown as one compact line ("You have incomplete
// items…") that opens into the list — a green tick for what is there, an empty
// circle and a way to it for what is not. Every company gives a return address
// and its return policy (a link, its own text or Aldoo's standard one); the return period and stock threshold only once
// it sells goods that travel. An optional item (free shipping) is listed but
// never holds it back. It is gone once everything required is in place. It
// replaced the notice about missing emails, and shows on every page of the
// portal until then.
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"
import React, { useEffect, useState } from "react"
import ProgressBar from "aldoo-ra/ProgressBar"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { ChevronDownIcon, ClipboardDocumentCheckIcon, DocumentTextIcon } from "@heroicons/react/24/outline"
import usePortalTexts from "./texts/usePortalTexts"
import { PORTAL } from "./texts/bundles"

const filled = (value) => typeof value === "string" && value.trim().length > 0

// how a company gives buyers its return terms (aldoo-com-api vendors/lib/return-policy): "link",
// "text" or "aldoo"; one from before the choice with a link counts as "link", "" is none yet
export const RETURN_POLICY_MIN_TEXT = 50
export const RETURN_POLICY_URL = /^https?:\/\/[^\s.]+\.[^\s]+$/i
export const returnPolicyModeOf = (org) => (["link", "text", "aldoo"].includes(org?.returnPolicyMode) ? org.returnPolicyMode : filled(org?.returnPolicyUrl) ? "link" : "")
// the way chosen, with what it needs: a link, or a text of RETURN_POLICY_MIN_TEXT characters or more
export const returnPolicyComplete = (org) => {
  const mode = returnPolicyModeOf(org)
  if (mode === "link") return RETURN_POLICY_URL.test(String(org.returnPolicyUrl || "").trim())
  if (mode === "text") return String(org.returnPolicyText || "").trim().length >= RETURN_POLICY_MIN_TEXT
  return mode === "aldoo"
}

/**
 * The checklist of a company: [{ key, label, done, section, tab, target }] —
 * `section` is the portal tab where it is filled in, `tab` the sub-tab of it
 * (the section's own URL param: company=…, delivery=…), `target` the field
 * there (its data-testid), which highlightField brings into view.
 */
// where the payout details are still missing: the first field that lacks something, in the order the
// Company tabs show them — the field itself (the EIK, the representative…), not the top of its block
// (the type of seller is always set: a company unless a person is chosen)
const DETAILS_FIELDS = [
  ["legalName", "profile-name"],
  ["companyId", "payout-company-id"],
  ["representative", "payout-representative"],
  ["personalId", "payout-personal-id"],
  ["dateOfBirth", "payout-dob"],
]
function payoutDetailsPlace(payouts) {
  const missing = payouts?.missing || []
  const detail = DETAILS_FIELDS.find(([key]) => missing.includes(key))
  if (detail) return { tab: { company: "details" }, target: detail[1] }
  if (missing.includes("country")) return { tab: { company: "address" }, target: "payout-country" }
  if (missing.includes("address")) return { tab: { company: "address" }, target: "payout-address" }
  if (missing.includes("email")) return { tab: { company: "contacts" }, target: "payout-email" }
  if (missing.includes("iban")) return { tab: { company: "payments" }, target: "payout-iban-row" }
  return { tab: { company: "details" }, target: "payout-entity-type" }
}

// at least one courier on, with a way of delivering on; a company has none until it adds one
// pickup from the shop counts only with a shop open (the API's rule: vendors/lib/sales-pauses)
export const shipsWithProvider = (org) =>
  Array.isArray(org?.shippingProviders) &&
  org.shippingProviders.some(
    (entry) =>
      entry?.enabled !== false &&
      (entry.providerId !== "shop" || (Array.isArray(org.shops) && org.shops.some((shop) => shop.active !== false))) &&
      (!entry.methods || !Object.keys(entry.methods).length || Object.values(entry.methods).some((method) => method?.enabled !== false))
  )

/**
 * The company checklist with where each item is filled in. `items`: the server's reading of it
 * (vendor {profile}.eligibility, aldoo-com-api vendors/lib/eligibility — the one source the
 * guide, the checklist and the marks all follow); each item given there gets its place from the
 * reading below. Without it (an older answer) the checklist is read here, the same way.
 */
export function eligibilityOf(org, { physical = false, payouts = null, items = null } = {}) {
  if (!org) return []
  const local = readEligibility(org, { physical: physical || (Array.isArray(items) && items.some((item) => item.key === "shippingProvider")), payouts })
  if (!Array.isArray(items) || !items.length) return local
  const places = new Map(local.map((item) => [item.key, item]))
  return items.map((item) => ({ ...(places.get(item.key) || { section: "company" }), key: item.key, label: item.label, done: !!item.done, ...(item.optional ? { optional: true } : { optional: undefined }) }))
}

function readEligibility(org, { physical = false, payouts = null } = {}) {
  const salesEmail = org.salesEmailMode === "own" ? org.salesEmail : org.email
  const items = [
    { key: "email", target: "contact-email", label: "Contact email", done: filled(org.email), section: "company", tab: { company: "contacts" } },
    { key: "salesEmail", target: "sales-email", label: "Email for orders", done: filled(salesEmail), section: "company", tab: { company: "contacts" } },
    { key: "contactName", target: "profile-contact-name", label: "Contact person", done: filled(org.contactName), section: "company", tab: { company: "contacts" } },
    { key: "phone", target: "profile-phone", label: "Phone", done: filled(org.phone), section: "company", tab: { company: "contacts" } },
    // who is paid and the bank account (Payouts); a company that gave only an IBAN before them is not held back
    { key: "payoutDetails", label: "Payout details (company and bank account)", done: payouts ? payouts.detailsComplete || (!payouts.profileExists && filled(org.iban)) : filled(org.iban), section: "company", ...payoutDetailsPlace(payouts) },
    // goods that travel: a courier first (the delivery's first tab)
    ...(physical ? [{ key: "shippingProvider", target: "add-shipping-provider", label: "At least one shipping provider", done: shipsWithProvider(org), section: "shipping", tab: { delivery: "providers" } }] : []),
    // every company: where goods come back and its return terms
    { key: "returnAddress", target: "return-address", label: "Return address for physical goods", done: filled(org.returnAddress), section: "shipping", tab: { delivery: "settings" } },
    // a link to its own page, its own text, or Aldoo's standard policy (returnPolicyComplete)
    { key: "returnPolicy", target: "return-policy", label: "Return policy", done: returnPolicyComplete(org), section: "shipping", tab: { delivery: "settings" } },
    // the payout contract: signed in Company → Contract, once Aldoo sent it
    { key: "payoutContract", target: "contract-sign-form", label: "Signed payout contract", done: !!payouts?.contractSigned && !payouts?.contractPending, section: "company", tab: { company: "contract" } },
    // goods that travel: a courier to deliver them (without one they are paused on the store), and what a buyer needs to send them back
    ...(physical
      ? [
          { key: "returnDays", target: "return-days", label: "Return period", done: Number(org.returnDays) >= 14, section: "shipping", tab: { delivery: "settings" } },
          { key: "lowStock", target: "low-stock-threshold", label: "Low stock threshold", done: Number(org.lowStockThreshold) > 0, section: "settings", tab: { settings: "orders" } },
          { key: "freeShipping", target: "free-shipping-amount", label: "Free shipping offer", done: Number(org.freeShippingThreshold?.amount) > 0, section: "shipping", tab: { delivery: "settings" }, optional: true },
        ]
      : []),
  ]
  return items
}

/** Whether Aldoo can reach the company at all: both its emails. */
export const reachable = (org) => eligibilityOf(org).filter((item) => item.key === "email" || item.key === "salesEmail").every((item) => item.done)

/**
 * The field an item is filled in, once its tab is on screen: scrolled to the
 * middle, focused, and its box lit for a moment so the eye finds it.
 */
export function highlightField(target, { tries = 30 } = {}) {
  if (typeof document === "undefined" || !target) return
  const find = (left) => {
    const field = document.querySelector(`[data-testid="${target}"]`)
    if (!field) return left > 0 && setTimeout(() => find(left - 1), 100)
    // a folded section (Section collapsible) opens first, then the field is brought into view
    const fold = field.closest('[data-collapsible="closed"]')
    if (fold) {
      fold.querySelector("[data-collapse-toggle]")?.click()
      return setTimeout(() => find(left), 50)
    }
    const box = field.closest("label") || field.parentElement || field
    box.scrollIntoView({ behavior: "smooth", block: "center" })
    const input = field.matches("input, textarea, select") ? field : field.querySelector("input, textarea, select")
    setTimeout(() => input?.focus({ preventScroll: true }), 350)
    // a square frame around the field and its label, a little outside them: drawn as an outline,
    // so nothing around it moves and no corner is rounded off
    const before = { outline: box.style.outline, outlineOffset: box.style.outlineOffset, backgroundColor: box.style.backgroundColor, boxShadow: box.style.boxShadow, transition: box.style.transition, alignSelf: box.style.alignSelf }
    box.style.transition = "outline-color .3s ease, background-color .3s ease"
    // the field's own height, not its grid row's
    box.style.alignSelf = "start"
    box.style.outline = "3px solid rgba(245, 158, 11, .85)"
    box.style.outlineOffset = "6px"
    box.style.boxShadow = "0 0 0 6px rgba(254, 243, 199, .9)"
    box.style.backgroundColor = "rgba(254, 243, 199, .9)"
    setTimeout(() => {
      box.style.outline = "3px solid transparent"
      box.style.boxShadow = before.boxShadow
      box.style.backgroundColor = before.backgroundColor
      setTimeout(() => Object.assign(box.style, before), 400)
    }, 2600)
  }
  find(tries)
}

// asked from elsewhere on the page (the contract's "required details"): the checklist opens and comes into view
export const CHECKLIST_OPEN = "vendor:checklist-open"
export const openChecklist = () => typeof window !== "undefined" && window.dispatchEvent(new Event(CHECKLIST_OPEN))

/**
 * Every detail filled in, the payout contract not signed yet: the step after the checklist, said
 * on its own — ready to sign (with the way to it), or on its way from Aldoo. Nothing once signed.
 */
function ContractNotice({ payouts, onFix }) {
  const t = usePortalTexts(PORTAL)
  if (!payouts || (payouts.contractSigned && !payouts.contractPending)) return null
  const pending = !!payouts.contractPending
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm ${pending ? "border-admin_warning bg-admin_warning/5 text-admin_warning" : "border-admin_bordered bg-admin_neutral_1/40 text-admin_text"}`} data-testid="contract-notice" data-pending={pending || undefined}>
      <DocumentTextIcon className={`w-5 h-5 shrink-0 ${pending ? "text-admin_warning" : "text-admin_text_secondary"}`} />
      <span className="flex-1 min-w-[220px]">
        <span className="font-semibold inline-flex items-center gap-1.5">
          {pending 
            ? t`Your payout contract is ready: review and sign it` 
            : t`Aldoo will send your contract`
          }
          <HelpHint helpKey="vendor.getting-started#contract" />
        </span>
        {!pending && <span className="block text-xs text-admin_text_secondary">
          {t`You will get an email when it is ready to sign.`}
        </span>}
      </span>
      {pending && onFix && (
        <button 
          type="button" 
          onClick={() => onFix("company", "contract-sign-form", { company: "contract" })} 
          className="font-semibold underline underline-offset-2 hover:text-admin_warning" 
          data-testid="contract-notice-sign"
        >
          {t`Review and sign`}
        </button>
      )}
    </div>
  )
}

/** The checklist line; `onFix(section, target)` opens the tab where an item is filled in and points at its field. */
export default function EligibilityChecklist({ org, physical = false, payouts = null, items: serverItems = null, onFix }) {
  const t = usePortalTexts(PORTAL)
  // collapsed at first; opened once by a link that asks for it (?checklist=open: the review
  // tab sends sellers here), the ask then taken out of the address so it does not stick
  const [open, setOpen] = useState(false)
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get("checklist") !== "open") return
    setOpen(true)
    url.searchParams.delete("checklist")
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash)
  }, [])
  useEffect(() => {
    const show = () => {
      setOpen(true)
      document.querySelector('[data-testid="eligibility-checklist"]')?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
    window.addEventListener(CHECKLIST_OPEN, show)
    return () => window.removeEventListener(CHECKLIST_OPEN, show)
  }, [])
  // the details to fill in; the contract is not one of them — it follows them (the notice below)
  const items = eligibilityOf(org, { physical, payouts, items: serverItems }).filter((item) => item.key !== "payoutContract")
  const required = items.filter((item) => !item.optional)
  const done = required.filter((item) => item.done).length
  if (!required.length) return null
  if (done === required.length) return <ContractNotice payouts={payouts} onFix={onFix} />
  return (
    <div className="rounded-lg border border-admin_warning bg-admin_warning/5 text-sm text-admin_warning" data-testid="eligibility-checklist">
      <button 
        type="button" 
        onClick={() => setOpen(!open)} 
        className="w-full flex items-center gap-3 px-4 py-3 text-left" 
        aria-expanded={open} 
        data-testid="eligibility-toggle"
      >
        <ClipboardDocumentCheckIcon className="w-5 h-5 shrink-0 text-admin_warning" />
        <span className="flex-1 min-w-0 font-semibold">
          {t`You have incomplete items in your company eligibility checklist`}
        </span>
        {/* how far along: the bar and "3 of 6 done", in the header whether open or not */}
        <span className="flex items-center gap-3 shrink-0">
          <span className="hidden sm:block w-32 md:w-48">
            <ProgressBar
              progress={done / required.length}
              variant="compact"
              styleOverrides={{ progressContainer: "h-2 w-full rounded-full bg-admin_progress/10", progressFill: "h-2 rounded-full bg-admin_progress transition-all duration-300" }}
            />
          </span>
          <span className="text-admin_warning whitespace-nowrap" data-testid="eligibility-count">
            {t("%s of %s done", done, required.length)}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="m-0 px-4 pb-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 list-none" data-testid="eligibility-items">
          {/* what the checklist is, from the help center */}
          <li className="sm:col-span-2 lg:col-span-3 flex items-center gap-1 text-xs text-admin_text_secondary"><HelpHint helpKey="vendor.getting-started#company" />{t`Company details`}</li>
          {/* only what is still to do: the done ones are in the count and the bar above */}
          {items.filter((item) => !item.done).map((item) => (
            <li key={item.key} className="flex items-center gap-2 min-w-0" data-testid={`eligibility-${item.key}`} data-done={item.done || undefined}>
              {item.done 
                ? <CheckCircleIcon className="w-5 h-5 shrink-0 text-admin_success" /> 
                : <span className="w-[18px] h-[18px] mx-px shrink-0 rounded-full border-2 border-admin_progress" aria-hidden />
              }
              {/* a missing item is its own way to the field that fills it */}
              {!item.done && onFix ? (
                <button
                  type="button"
                  onClick={() => onFix(item.section, item.target, item.tab)}
                  className={`text-left underline underline-offset-2 hover:text-admin_warning/80 ${item.optional ? "text-admin_warning" : "font-medium"}`}
                  data-testid={`eligibility-fix-${item.key}`}
                >
                  {t(item.label)}
                  {item.optional && <span className="ml-1 text-xs no-underline">({t`optional`})</span>}
                </button>
              ) : (
                <span className={item.done || item.optional ? "text-admin_warning" : "font-medium"}>
                  {t(item.label)}
                  {item.optional && <span className="ml-1 text-xs">({t`optional`})</span>}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
